// @flow

import React, { type Node } from 'react';

import { useUniqueId } from '@tectonics/hooks/use-unique-id';
import { useSubmitting } from '@tectonics/prs/submitting';
import { amountToCurrencyString } from '@tectonics/prs/money';
import { type Country } from '@tectonics/prs/country';
import type { CustomReceiptsFetchStatus } from '@tectonics/prs/custom-receipts';
import { useCustomReceiptsFetchStatus } from '@tectonics/prs/custom-receipts';
import { useUserInputAsSelect } from '@tectonics/prs/user-input-values';
import { getCountry, getReceipts, useIncomingPayment } from '@tectonics/prs/incoming-payment';
import { useTranslation } from 'react-i18next';
import type { InstalmentsStrategy, Receipt } from '@tectonics/prs/receipts';
import { getInstalmentsFromReceipts, getSafeInstalmentsStrategy } from '@tectonics/prs/receipts';
import { getInstalmentsPlan, getReceiptAmountLocalTotal, getSafeInstalmentsPlan } from '@tectonics/prs/receipts/receipt';
import { getAmountPartialFirst, getInstalmentsNumber } from '@tectonics/prs/receipts/receipt/instalments-plan';

import { Empty, Select, SelectOption } from '../../../../../atoms';
import { getMoneyFormat } from '../../../../../commons/money-format';

export function FieldCardInstalments(): Node {
  const inputId = useUniqueId();
  const incomingPayment = useIncomingPayment();
  const [selectedInstalment, handleChangeSelectedInstalment] = useUserInputAsSelect('selectedInstalment');
  const submitting = useSubmitting();

  const receipts = getReceipts(incomingPayment);
  const instalments = getInstalmentsFromReceipts(receipts);
  const country = getCountry(incomingPayment);

  const instalmentsStrategy = getSafeInstalmentsStrategy(receipts);
  const customReceiptsFetchStatus = useCustomReceiptsFetchStatus();

  if (shouldHideInstalments(instalmentsStrategy, customReceiptsFetchStatus, instalments))
    return <Empty />;

  return (
    <div data-test-name="card-instalments">
      <label htmlFor={inputId}>
        <InstalmentsLabel />
      </label>
      <Select
        id={inputId}
        onChange={handleChangeSelectedInstalment}
        value={selectedInstalment}
        readOnly={submitting || customReceiptsFetchStatus === 'fetching'}
        disabled={customReceiptsFetchStatus === 'failed'}
        name="card-instalments"
      >

        {instalmentsStrategy === 'from-invoice' &&
          <InstalmentsOptions instalments={instalments} country={country} />
        }

        {instalmentsStrategy === 'dynamic-card-bin' &&
          <InstalmentsOptionsFromCustomReceipts
            country={country}
            customReceiptsFetchStatus={customReceiptsFetchStatus}
            instalments={instalments}
          />
        }
      </Select>
    </div>
  );
}

type InstalmentsOptionsProps = {|
  instalments: ImmutableArray<Receipt>,
  country: Country,
|};

function InstalmentsOptions({ instalments, country }: InstalmentsOptionsProps): Node {
  return instalments.map(instalment => {
    const instalmentsPlan = getSafeInstalmentsPlan(instalment);
    const instalmentsNumber = getInstalmentsNumber(instalmentsPlan);

    return (
      <SelectOption
        value={instalmentsNumber}
        key={instalmentsNumber}
      >
        {getInstalmentOptionText(instalment, instalmentsNumber, country)}
      </SelectOption>
    );
  });
}

type InstalmentsOptionsFromCustomReceiptsProps = {|
  country: Country,
  customReceiptsFetchStatus: CustomReceiptsFetchStatus,
  instalments: ImmutableArray<Receipt>,
|};

function InstalmentsOptionsFromCustomReceipts(props: InstalmentsOptionsFromCustomReceiptsProps): Node {
  const { customReceiptsFetchStatus, instalments, country } = props;

  switch(customReceiptsFetchStatus) {
    case 'ready':
      return <InstalmentsOptions instalments={instalments} country={country} />;
    case 'fetching':
      return (
        <SelectOption>
          <FetchingCustomReceiptsText />
        </SelectOption>
      );
    case 'failed':
      return (
        <SelectOption>
          <FetchCustomReceiptsFailedText />
        </SelectOption>
      );
    default:
      return '';
  }
}

function FetchingCustomReceiptsText(): Node {
  const { t } = useTranslation('opened-page');
  return t('volcanes.form.instalments.fetching');
}

function FetchCustomReceiptsFailedText(): Node {
  const { t } = useTranslation('opened-page');
  return t('volcanes.form.instalments.fetchFailed');
}

function InstalmentsLabel(): Node {
  const { t } = useTranslation('opened-page');
  return t('volcanes.form.instalments.label');
}

function getInstalmentOptionText(instalment: Receipt, instalmentsNumber: number, country: Country): string {
  const formatAmount = amountToCurrencyString(getMoneyFormat('br'));
  const instalmentsPlan = getSafeInstalmentsPlan(instalment);

  const totalStringToDisplay =
    instalmentsNumber > 1 ? `(Total: ${formatAmount(getReceiptAmountLocalTotal(instalment))})` : '';
  const firstInstalment = formatAmount(getAmountPartialFirst(instalmentsPlan));

  switch (country) {
    case 'br':
    case 'ar':
    case 'bo':
    case 'cl':
    case 'co':
    case 'ec':
    case 'pe':
    case 'uy':
    case 'py':
    case 'pa':
    case 'cr':
    case 'sv':
    case 'gt':
    case 'do':
      return `${instalmentsNumber}x de ${firstInstalment} ${totalStringToDisplay}`;

    case 'mx':
      return `${instalmentsNumber} ${instalmentsNumber === 1 ? 'mes de' : 'meses de'} ${firstInstalment} ${totalStringToDisplay}`;

    default:
      throw new Error(`InstalmentsField: field text not mapped for country: ${country}`);
  }
}

function shouldHideInstalments(
  instalmentsStrategy: InstalmentsStrategy,
  customReceiptsFetchStatus: CustomReceiptsFetchStatus,
  instalments: ?ImmutableArray<Receipt>,
): boolean {
  if((instalmentsStrategy === 'dynamic-card-bin') && (customReceiptsFetchStatus === 'not-fetched'))
    return true;

  if (!instalments || !instalments.length)
    return true;

  if (instalments.length === 1) {
    const plan = getInstalmentsPlan(instalments[0]);
    return plan != null && plan.instalments === 1;
  }

  return false;
}
